import type { FinanceHomeWeek } from "@ibada/shared-types/overview"

const DAY_MS = 24 * 60 * 60 * 1000

/** Whole days between an ISO instant and now, never negative. */
export function daysSince(iso: string | null, now: Date): number | null {
  if (!iso) return null
  const at = new Date(iso).getTime()
  if (!Number.isFinite(at)) return null
  return Math.max(0, Math.floor((now.getTime() - at) / DAY_MS))
}

/**
 * A money string as a short whole-number label for a chart ("1 240"). Chart geometry and its labels are the one
 * place a money string becomes a number: the exact amount stays in the tooltip, formatted from the string.
 */
export function chartAmountLabel(value: string, locale: string): string {
  const numeric = Number(value)
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Number.isFinite(numeric) ? numeric : 0)
}

export type MirroredBar = {
  weekStart: string
  income: string
  expense: string
  /** Bar heights as a fraction (0 to 1) of the half-plot, against the largest value of either series. */
  incomeRatio: number
  expenseRatio: number
}

/** Geometry for the mirrored weekly chart: income above the baseline, expenses below, on ONE shared scale. */
export function mirroredBars(weeks: FinanceHomeWeek[]): MirroredBar[] {
  const values = weeks.flatMap((week) => [Number(week.income), Number(week.expense)]).filter(Number.isFinite)
  const max = Math.max(0, ...values)
  return weeks.map((week) => ({
    weekStart: week.weekStart,
    income: week.income,
    expense: week.expense,
    incomeRatio: max > 0 ? Math.max(0, Number(week.income)) / max : 0,
    expenseRatio: max > 0 ? Math.max(0, Number(week.expense)) / max : 0,
  }))
}

/** "Esther N." style short name for compact tiles. */
export function shortName(firstName: string, lastName: string | null): string {
  const initial = lastName?.trim().charAt(0)
  return initial ? `${firstName} ${initial.toUpperCase()}.` : firstName
}

export function fullName(firstName: string, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(" ")
}

/** A `YYYY-MM-DD` calendar day as a local Date at noon, so formatting in any zone keeps the same day. */
export function calendarDayToDate(day: string): Date {
  return new Date(`${day}T12:00:00`)
}

/** A `YYYY-MM` month as a Date in that month. */
export function monthKeyToDate(month: string): Date {
  return new Date(`${month}-15T12:00:00`)
}
